import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import moment from "moment";
import IndexApp from "../../Components/IndexApp";
import ClientSessionRepository, { ClientDashboardSummary, ClientSessionInfo } from "../../Repositories/ClientSessionRepository";
import DocumentRepository, { DocumentInfo } from "../../Repositories/DocumentRepository";
import { MAX_COURSES_PER_MONTH } from "../../Utils/monthlyQuota";
import { VT_MONTHLY_CEILING_TTC } from "../../Utils/VerifiedTrainerAmounts";
import { capitalizeFirstLetter } from "../../Utils/Function";
import { getSupportContact } from "../../config/supportContact";
import { DV2 } from "../DeclarationV2/theme";
import { euros, monthLabel, plural } from "../DeclarationV2/format";
import { vocab } from "../DeclarationV2/vocabulary";
import { Button, Card, KeyValue, TextLink, useDv2Theme } from "../DeclarationV2/layout/Ui";
import { documentDateLabel, documentTitle } from "../DocumentsV2/documentRows";
import { clientCoursesTabPath, clientDocumentsPath, clientHelpPath, clientSettingsTabPath } from "./clientPaths";
import { sessionAmountLabel, sessionCountdown, sessionWhen, sessionWho } from "./clientCourseRows";

const UPCOMING_SHOWN = 5;
const DOCUMENTS_SHOWN = 3;

/**
 * Accueil du client repensé (maquette 12b du 2 novembre 2026) : un bonjour
 * qui dit ce qui va se passer, les prochains cours, « comment ça marche »
 * tant qu'aucun cours n'est prévu, les derniers documents ; à droite le mois
 * en cours (cours suivis, montant payé, avance immédiate), l'état de l'avance
 * immédiate et le contact de l'équipe. Mêmes appels que l'ancien accueil
 * (`ClientHome`, conservé dans le code) : résumé du tableau de bord, séances
 * à venir et documents.
 */
const ClientHomeV2 = () => {
    const navigate = useNavigate();
    const { primary, fontFamily, text, brandName } = useDv2Theme();
    const client = useSelector((state: any) => state.user?.currentUser?.client);
    const clientId: number | undefined = client?.id;
    const contact = getSupportContact();

    const [loading, setLoading] = useState(true);
    const [summary, setSummary] = useState<ClientDashboardSummary | null>(null);
    const [upcoming, setUpcoming] = useState<ClientSessionInfo[]>([]);
    const [documents, setDocuments] = useState<DocumentInfo[]>([]);

    useEffect(() => {
        if (!clientId) return undefined;
        let cancelled = false;
        Promise.allSettled([
            ClientSessionRepository.getSummary(clientId),
            ClientSessionRepository.getUpcoming(clientId, 0, UPCOMING_SHOWN),
            DocumentRepository.getClientDocuments(clientId),
        ]).then(([summaryResult, upcomingResult, documentsResult]) => {
            if (cancelled) return;
            setSummary(summaryResult.status === "fulfilled" ? summaryResult.value : null);
            setUpcoming(upcomingResult.status === "fulfilled" ? (upcomingResult.value?.sessions ?? []) : []);
            setDocuments(documentsResult.status === "fulfilled" ? (documentsResult.value?.documents ?? []).slice(0, DOCUMENTS_SHOWN) : []);
            setLoading(false);
        });
        return () => { cancelled = true; };
    }, [clientId]);

    const aiciActive = !!client?.idClientTiersPrestation;
    const upcomingCount = summary?.upcomingCount ?? upcoming.length;
    const month = summary?.month;
    const monthSessions = month?.sessionsCount ?? 0;
    const monthTotal = month?.totalAmount ?? 0;
    const monthAdvance = aiciActive ? (month?.estimatedTaxCredit ?? 0) : 0;
    const monthPaid = Math.max(0, monthTotal - monthAdvance);
    const hasAnyCourse = upcomingCount > 0 || monthSessions > 0;
    const next = upcoming[0] ?? summary?.nextSession ?? null;
    const firstName = capitalizeFirstLetter(client?.firstName ?? "");

    // ── Phrase d'accueil ────────────────────────────────────────────────────
    const greeting = (() => {
        if (loading) return "Chargement de votre espace…";
        if (!hasAnyCourse) return `Votre compte est prêt. Dès que ${vocab.yourTeachr()} déclare ${vocab.aCourse()}, ${vocab.theCourse().startsWith("la") ? "elle" : "il"} apparaît ici et vous avez 48 heures pour ${vocab.theCourse().startsWith("la") ? "la" : "le"} valider.`;
        const parts: string[] = [];
        if (upcomingCount > 0) parts.push(`${plural(upcomingCount, vocab.course(), vocab.courses())} à venir`);
        if (next) parts.push(`prochain ${sessionWhen(next).charAt(0).toLowerCase()}${sessionWhen(next).slice(1)}`);
        if (monthSessions > 0) parts.push(`${plural(monthSessions, vocab.course(), vocab.courses())} ce mois`);
        return `${capitalizeFirstLetter(parts.join(", "))}.`;
    })();

    const cardTitle = (title: string, right?: React.ReactNode) => (
        <div className="flex items-center justify-between gap-3">
            <h2 style={{ fontSize: 16, fontWeight: 800, margin: 0 }}>{title}</h2>
            {right}
        </div>
    );

    const step = (n: number, title: string, body: string) => (
        <div>
            <div aria-hidden style={{ width: 30, height: 30, borderRadius: "50%", background: primary, color: "#fff", display: "grid", placeItems: "center", fontWeight: 800, fontSize: 14 }}>{n}</div>
            <div style={{ fontWeight: 800, fontSize: 14, marginTop: 10 }}>{title}</div>
            <div style={{ fontSize: 13, color: DV2.muted, lineHeight: 1.5, marginTop: 4 }}>{body}</div>
        </div>
    );

    return (
        <IndexApp
            navBar={{ index: 0 }}
            component={
                <main className="px-4" style={{ background: DV2.pageBg, minHeight: "calc(100vh - 64px)", fontFamily, color: text }}>
                    <div className="mx-auto w-full px-4 sm:px-6 py-4 lg:py-8 flex flex-col gap-6">
                        <header className="min-w-0">
                            <h1 style={{ fontSize: 26, fontWeight: 800, margin: 0, lineHeight: 1.2 }}>Bonjour {firstName}</h1>
                            <div style={{ fontSize: 14, color: DV2.muted, marginTop: 4, maxWidth: 720 }}>{greeting}</div>
                        </header>

                        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px] gap-6 items-start">
                            {/* Colonne gauche */}
                            <div className="flex flex-col gap-4 min-w-0">
                                <Card padding={22}>
                                    {cardTitle(`Vos prochains ${vocab.courses()}`, <TextLink label={`Tous mes ${vocab.courses()}`} onClick={() => navigate(clientCoursesTabPath("a-venir"))} />)}
                                    {loading ? (
                                        <div style={{ marginTop: 14, fontSize: 14, color: DV2.muted }}>Chargement…</div>
                                    ) : upcoming.length === 0 ? (
                                        <div className="flex items-start gap-4" style={{ marginTop: 14, background: DV2.pageBg, borderRadius: 14, padding: "16px 18px" }}>
                                            <div aria-hidden style={{ width: 44, height: 44, borderRadius: 12, background: "#fff", border: `1px solid ${DV2.border}`, display: "grid", placeItems: "center", fontSize: 20, flexShrink: 0 }}>📅</div>
                                            <div>
                                                <div style={{ fontWeight: 800, fontSize: 15 }}>Aucun {vocab.course()} prévu pour le moment</div>
                                                <div style={{ fontSize: 13, color: DV2.muted, lineHeight: 1.5, marginTop: 2 }}>
                                                    C'est {vocab.yourTeachr()} qui déclare les {vocab.courses()}. Vous n'avez rien à saisir, seulement à valider quand {vocab.theCourse().startsWith("la") ? "elle" : "il"} le fait.
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div style={{ marginTop: 10 }}>
                                            {upcoming.map((s, i) => {
                                                const countdown = sessionCountdown(s);
                                                const amount = sessionAmountLabel(s);
                                                return (
                                                    <div key={s.sessionId ?? i} className="flex items-center gap-3" style={{ padding: "12px 0", borderTop: i === 0 ? "none" : `1px solid ${DV2.borderSoft}` }}>
                                                        <div style={{ flex: 1, minWidth: 0 }}>
                                                            <div style={{ fontWeight: 800, fontSize: 14 }}>{sessionWhen(s)}</div>
                                                            <div style={{ fontSize: 13, color: DV2.muted, marginTop: 2 }}>{sessionWho(s)}</div>
                                                        </div>
                                                        <div style={{ textAlign: "right", flexShrink: 0 }}>
                                                            {countdown && <div style={{ fontSize: 12, fontWeight: 800, color: primary }}>{countdown}</div>}
                                                            {amount && <div style={{ fontSize: 13, color: DV2.muted }}>{amount}</div>}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                            {upcomingCount > upcoming.length && (
                                                <div style={{ fontSize: 13, color: DV2.muted, paddingTop: 8 }}>
                                                    Et {plural(upcomingCount - upcoming.length, `autre ${vocab.course()}`, `autres ${vocab.courses()}`)} à venir.
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </Card>

                                {!loading && upcoming.length === 0 && (
                                    <Card padding={22}>
                                        {cardTitle("Comment ça marche pour vous")}
                                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5" style={{ marginTop: 16 }}>
                                            {step(1, `${capitalizeFirstLetter(vocab.yourTeachr())} déclare`, `${vocab.theCourse().startsWith("la") ? "Elle" : "Il"} saisit la date, la durée et le prix ${vocab.ofTheCourse()}.`)}
                                            {step(2, "Vous validez", "Vous recevez une notification et avez 48 heures pour accepter ou contester.")}
                                            {step(3, aiciActive ? "Vous payez la moitié" : "Vous payez", aiciActive
                                                ? "L'URSSAF règle l'autre moitié grâce à l'avance immédiate du crédit d'impôt."
                                                : "Activez l'avance immédiate pour ne payer que la moitié : l'URSSAF règle le reste.")}
                                        </div>
                                    </Card>
                                )}

                                <Card padding={22}>
                                    {cardTitle("Vos documents", <TextLink label="Tout voir" onClick={() => navigate(clientDocumentsPath())} />)}
                                    {loading ? (
                                        <div style={{ marginTop: 14, fontSize: 14, color: DV2.muted }}>Chargement…</div>
                                    ) : documents.length === 0 ? (
                                        <div style={{ fontSize: 13, color: DV2.muted, lineHeight: 1.55, marginTop: 10 }}>
                                            Vos devis, factures et attestations fiscales apparaîtront ici au fil de vos {vocab.courses()}. L'attestation annuelle pour vos impôts est disponible chaque début d'année.
                                        </div>
                                    ) : (
                                        <div style={{ marginTop: 10 }}>
                                            {documents.map((doc, i) => (
                                                <div key={doc.id ?? i} className="flex items-center gap-3" style={{ padding: "10px 0", borderTop: i === 0 ? "none" : `1px solid ${DV2.borderSoft}` }}>
                                                    <div aria-hidden style={{ width: 36, height: 40, borderRadius: 8, background: DV2.tint, color: primary, display: "grid", placeItems: "center", fontSize: 10, fontWeight: 800, flexShrink: 0 }}>PDF</div>
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                        <div style={{ fontWeight: 800, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{documentTitle(doc)}</div>
                                                        <div style={{ fontSize: 12, color: DV2.muted }}>{documentDateLabel(doc)}{doc.amount != null ? `, ${euros(doc.amount)}` : ""}</div>
                                                    </div>
                                                    <TextLink label="Ouvrir" onClick={() => window.open(doc.url, "_blank", "noopener")} />
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </Card>
                            </div>

                            {/* Colonne droite */}
                            <div className="flex flex-col gap-4">
                                <Card padding={22}>
                                    {cardTitle(capitalizeFirstLetter(monthLabel(moment())))}
                                    <div className="flex flex-col gap-3" style={{ marginTop: 14, fontSize: 14 }}>
                                        <KeyValue label={`${capitalizeFirstLetter(vocab.courses())} suivis`} value={loading ? "…" : monthSessions === 0 ? "Aucun" : String(monthSessions)} />
                                        <KeyValue label="Montant payé" value={loading ? "…" : euros(monthPaid)} />
                                        <KeyValue label="Avance immédiate" value={loading ? "…" : euros(monthAdvance)} />
                                    </div>
                                    <div style={{ fontSize: 12, color: DV2.muted, lineHeight: 1.5, marginTop: 12 }}>
                                        L'avance immédiate couvre 50 % de chaque {vocab.course()}, dans la limite de {MAX_COURSES_PER_MONTH} {vocab.courses()} et {euros(VT_MONTHLY_CEILING_TTC, 0)} {vocab.declared()}s par mois.
                                    </div>
                                </Card>

                                {aiciActive ? (
                                    <Card padding={20} style={{ background: DV2.successBg, borderColor: "#BFE3CB" }}>
                                        <div className="flex items-start gap-3">
                                            <div aria-hidden style={{ width: 28, height: 28, borderRadius: "50%", background: DV2.success, color: "#fff", display: "grid", placeItems: "center", fontSize: 14, flexShrink: 0 }}>✓</div>
                                            <div>
                                                <div style={{ fontWeight: 800, fontSize: 15, color: DV2.success }}>Avance immédiate active</div>
                                                <div style={{ fontSize: 13, color: DV2.textSoft, lineHeight: 1.5, marginTop: 2 }}>Votre compte URSSAF est relié. Vous ne paierez que la moitié de vos {vocab.courses()}.</div>
                                            </div>
                                        </div>
                                    </Card>
                                ) : (
                                    <Card padding={20} style={{ background: DV2.warningBg, borderColor: "#F3D9B5" }}>
                                        <div style={{ fontWeight: 800, fontSize: 15, color: DV2.warning }}>Avance immédiate à activer</div>
                                        <div style={{ fontSize: 13, color: DV2.textSoft, lineHeight: 1.5, marginTop: 4 }}>
                                            Ne payez que la moitié de vos {vocab.courses()} : le crédit d'impôt est déduit à la source par l'URSSAF.
                                        </div>
                                        <div style={{ marginTop: 12 }}>
                                            <Button label="Activer maintenant" size="sm" onClick={() => navigate(clientSettingsTabPath("avance-immediate"))} />
                                        </div>
                                    </Card>
                                )}

                                <Card padding={22}>
                                    {cardTitle("Une question")}
                                    <div style={{ fontSize: 13, color: DV2.muted, lineHeight: 1.5, marginTop: 8 }}>L'équipe {brandName} vous répond en général dans la journée.</div>
                                    <div className="flex flex-wrap items-center gap-3" style={{ marginTop: 12 }}>
                                        <Button label="Nous écrire" variant="secondary" size="sm" onClick={() => navigate(clientHelpPath())} />
                                        <TextLink label={contact.email} href={`mailto:${contact.email}`} size={14} />
                                    </div>
                                </Card>
                            </div>
                        </div>
                    </div>
                </main>
            }
        />
    );
};

export default ClientHomeV2;
